import { grantableRoles } from './manifest';
import type { ModuleManifest } from './manifest';

/**
 * A module's views. Deliberately free of any Vue dependency — the core owns
 * the router and maps these into it, so the contract only needs to describe a
 * lazily-imported component and the role that may see it.
 */

/**
 * Where a module's settings page lives, relative to `/m/<id>`. Fixed so that
 * every module's settings are in the same place, and so the manifest and the
 * ui can be checked against each other at all.
 */
export const SETTINGS_PATH = 'settings';

/**
 * The name the core registers a module's route under.
 *
 * **Neither side writes the string**, which is the same argument the ownership
 * key builders make. A module linking to its own detail page has to name a
 * route the core invented, and a module cannot import the core — so a literal
 * here and a template there is a link that resolves to nothing, silently, in
 * exactly the way `/account` did.
 */
export function moduleRouteName(moduleId: string, path: string): string {
	return `m:${moduleId}:${path}`;
}

export interface UiRoute<TRole extends string = string> {
	/** Relative to `/m/<id>`. Empty string is the module's index. */
	readonly path: string;
	readonly title: string;
	readonly requiredRole: TRole;
	/** `() => import('./views/thing.vue')`. Opaque here on purpose. */
	readonly component: () => Promise<unknown>;
}

export interface ModuleUi<TRole extends string = string> {
	readonly routes: readonly UiRoute<TRole>[];
}

export function defineUi<const TRole extends string>(ui: ModuleUi<TRole>): ModuleUi<TRole> {
	return ui;
}

export class UiInvalidError extends Error {
	readonly code = 'ui_invalid';
}

/**
 * What the types cannot reach: `nav` and `routes` are declared in two
 * different files and nothing makes them agree. A nav entry whose path has no
 * route is a link to a 404, and a route the manifest never names is reachable
 * only by typing the URL — both compile perfectly.
 *
 * Takes the manifest rather than living on it because `./manifest` stays
 * dependency-free, and this is the one check that needs to see both surfaces.
 */
export function assertUiValid(manifest: ModuleManifest, ui: ModuleUi): void {
	const problems: string[] = [];
	const allowed = new Set<string>(grantableRoles(manifest));
	const paths = new Set<string>();

	for (const route of ui.routes) {
		// Paths are relative to `/m/<id>`; a leading slash would resolve to
		// the application root and silently escape the module.
		if (route.path.startsWith('/')) {
			problems.push(`route path '${route.path}' must be relative to the module`);
		}
		if (paths.has(route.path)) {
			problems.push(`route path '${route.path}' is declared twice`);
		}
		paths.add(route.path);

		if (!allowed.has(route.requiredRole)) {
			problems.push(`route '${route.path}' requires unknown role '${route.requiredRole}'`);
		}
	}

	/**
	 * A settings schema with nowhere to edit it is configurable only by
	 * writing the row by hand, which is the `D92` shape — declared, and
	 * reachable by nobody.
	 *
	 * The path is fixed rather than free (`D109`): a module's settings sitting
	 * at the same place in every module is worth more than the freedom to call
	 * it something else, and it makes the pairing checkable at all.
	 */
	if (manifest.settings !== undefined && !manifest.nav.some((e) => e.path === SETTINGS_PATH)) {
		problems.push(
			`a settings schema is declared, but no nav entry at '${SETTINGS_PATH}' edits it`,
		);
	}

	for (const entry of manifest.nav) {
		if (!paths.has(entry.path)) {
			problems.push(
				`nav entry '${entry.title}' points at '${entry.path}', which has no route`,
			);
		}
	}

	if (problems.length > 0) {
		throw new UiInvalidError(problems.join('; '));
	}
}
